import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import UserInfo from '../../UserInfo';
import Routes from '../../routes';

// AB#17128 AC12/AC14: one System Manager and one Region / Division (17146 AC 1), shared by the landing page and every drill-down
const ComplianceFilterContext = createContext(null);

const storageKey = () => `complianceCenterFilter/${UserInfo.instanceId}/${UserInfo.userId}`;

const readStored = () => {
    try {
        const saved = JSON.parse(sessionStorage.getItem(storageKey()));
        return {
            regionDivisionIds: Array.isArray(saved?.regionDivisionIds) ? saved.regionDivisionIds : [],
            systemManagerIds: Array.isArray(saved?.systemManagerIds) ? saved.systemManagerIds : [],
            regionDivision: typeof saved?.regionDivision === 'string' ? saved.regionDivision : null,
            systemManager: typeof saved?.systemManager === 'string' ? saved.systemManager : null,
        };
    } catch {
        return { regionDivisionIds: [], systemManagerIds: [], regionDivision: null, systemManager: null };
    }
};

// The landing page filters by name and the drill-down by id; a name can cover several ids
const idsNamed = (list, name) => (name ? (list || []).filter((o) => o.name === name).map((o) => o.id) : []);
const nameOf = (list, ids) => (list || []).find((o) => ids.includes(o.id))?.name ?? null;

// A newly ticked drill-down option replaces the choice; unticking clears it
const pickedName = (list, prevIds, nextIds) => {
    const added = nextIds.find((id) => !prevIds.includes(id));
    return added === undefined ? null : nameOf(list, [added]);
};

const choose = (options, regionDivision, systemManager) => ({
    regionDivision: regionDivision || null,
    systemManager: systemManager || null,
    regionDivisionIds: idsNamed(options.regionDivisions, regionDivision),
    systemManagerIds: idsNamed(options.systemManagers, systemManager),
});

export const ComplianceFilterProvider = ({ children }) => {
    const [filter, setFilter] = useState(readStored);
    const [options, setOptions] = useState({ regionDivisions: [], systemManagers: [] });

    useEffect(() => {
        sessionStorage.setItem(storageKey(), JSON.stringify(filter));
    }, [filter]);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await fetch(Routes.ComplianceCenterRegulation.FilterOptions(), { headers: { Accept: 'application/json' } });
                if (!response.ok) return;
                const loaded = await response.json();
                setOptions(loaded);
                // Ids follow the chosen name, so a stored id that's no longer offered can't filter invisibly
                setFilter((prev) => choose(
                    loaded,
                    prev.regionDivision ?? nameOf(loaded.regionDivisions, prev.regionDivisionIds),
                    prev.systemManager ?? nameOf(loaded.systemManagers, prev.systemManagerIds),
                ));
            } catch (error) {
                console.error('Failed to load Compliance Center filter options', error);
            }
        };
        load();
    }, []);

    // The landing page's 17146 filter shape; stable while the names hold so its requests don't repeat
    const pageFilter = useMemo(
        () => ({ systemManager: filter.systemManager, regionDivision: filter.regionDivision }),
        [filter.systemManager, filter.regionDivision]
    );

    const value = useMemo(() => ({
        ...filter,
        options,
        pageFilter,
        setPageFilter: (next) => setFilter(() => choose(options, next.regionDivision, next.systemManager)),
        setRegionDivisionIds: (ids) => setFilter((prev) => choose(options, pickedName(options.regionDivisions, prev.regionDivisionIds, ids), prev.systemManager)),
        setSystemManagerIds: (ids) => setFilter((prev) => choose(options, prev.regionDivision, pickedName(options.systemManagers, prev.systemManagerIds, ids))),
        // DataGrid staticFilters / request body shape the API expects
        requestFilters: [
            ...filter.regionDivisionIds.map((id) => ({ property: 'regionDivisionId', value: String(id) })),
            ...filter.systemManagerIds.map((id) => ({ property: 'systemManagerId', value: String(id) })),
        ],
        queryString: [
            ...filter.regionDivisionIds.map((id) => `regionDivisionId=${encodeURIComponent(id)}`),
            ...filter.systemManagerIds.map((id) => `systemManagerId=${encodeURIComponent(id)}`),
        ].join('&'),
    }), [filter, options, pageFilter]);

    return (
        <ComplianceFilterContext.Provider value={value}>
            {children}
        </ComplianceFilterContext.Provider>
    );
};

export const useComplianceFilter = () => useContext(ComplianceFilterContext);
